import React, { useEffect, useMemo, } from 'react';
import { format as formatDate } from 'date-fns';
import { keyBy, isEmpty, sortBy, orderBy, isEqual, uniq, chunk, groupBy, fromPairs } from 'lodash';
import { Button, Input } from 'reactstrap';
import numeral from 'numeral';
import { useList } from 'react-use';

import firebase, { functions } from '../../firebase';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionsFetchInTenant from '../hooks/useCollectionsFetchInTenant';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useCollectionFetchInTenant from '../hooks/useCollectionFetchInTenant';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import AdminEventHeader from '../AdminEventHeader';
import TenantLink from '../TenantLink';
import ModelFormModal from '../modals/ModelFormModal';
import ModalButton from '../ModalButton';
import QueryBoolean from '../QueryBoolean';
import { toast } from 'react-toastify';
import useTenant from '../hooks/useTenant';

const db = firebase.firestore();
const productsRef = db.collection('products');
const ordersRef = db.collection('orders');
const sendEmailToEventFollowersUsers = functions.httpsCallable('sendEmailToEventFollowersUsers');

export default AdminPage(function AdminEventFollowers(props) {
  const {
    user,
    match: {
      params: { eventId },
    },
    addBreadNavValues,
  } = props;

  const [selectedIds, { push: selectId, removeAt: removeSelectedIdAt, set: setSelectedIds }] = useList([]);
  const unselectId = id => removeSelectedIdAt(selectedIds.indexOf(id));
  const tenant = useTenant();
  const queryParams = useQueryParams();

  const event = useDocumentSubscription(db.collection('events').doc(eventId), [eventId]);
  
  // eventLectureAddNotifications コレクションからフォロワーを取得
  const _followers = useCollectionSubscription(
    db.collection('eventLectureAddNotifications').where('eventId', '==', eventId),
    [eventId]
  );
  const followers = useMemo(_ => orderBy(_followers, _ => _.createdAt.toDate(), 'desc'), [_followers]);

  // 参加済みエントリーを取得してフィルタリング用
  const entries = useCollectionsFetchInTenant(followers.map(_ => event?.ref.collection('entries').where('createdBy.uid', '==', _.createdBy.uid).limit(1)), [followers, event]);

  // 本体購入の有無を判定するための注文を取得
  const products = useCollectionFetchInTenant(productsRef.where('isBody', '==', true));
  const productsById = keyBy(products, 'id');
  const uniqueUids = uniq(followers.map(_ => _.createdBy.uid));
  const ordersRefs = chunk(uniqueUids, 10).map(_ =>
    ordersRef.where('tenantId', '==', tenant.id).where('createdBy.uid', 'in', _)
  );
  const { items: orders, isLoading: isLoadingOrders } = useCollectionsFetch(ordersRefs, [followers], { detail: true });
  const ordersGroupedByUid = groupBy(orders, _ => _.createdBy.uid);
  const hasBodyOrderByUid = fromPairs(
    Object.entries(ordersGroupedByUid).map(([uid, userOrders]) => [uid, userOrders.some(_ => _.orderItems.some(_ => productsById[_.productId]?.isBody))])
  );

  let filteredFollowers = followers;
  if (queryParams.showsEntried !== '1') {
    const entriedUserIds = entries.map(_ => _.createdBy.uid);
    filteredFollowers = filteredFollowers.filter(_ => !entriedUserIds.includes(_.createdBy.uid));
  }
  if (queryParams.unpurchasedOnly === '1') {
    filteredFollowers = filteredFollowers.filter(_ => !hasBodyOrderByUid[_.createdBy.uid]);
  }

  const rows = filteredFollowers;

  const totalCount = rows.length;

  const isSelecting = selectedIds.length > 0;
  const isAllSelected = isEqual(sortBy(selectedIds), sortBy(rows.map(_ => _.id)));

  const onClickSend = async ({ subject, text }, { onClickClose }) => {
    const confirmMessage = `${selectedIds.length}件のメールを送信しますか？\n\n件名: ${subject}\n本文: ${text}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await sendEmailToEventFollowersUsers({
        eventId,
        followerIds: selectedIds,
        subject,
        text
      });

      await event.ref.collection('emailHistories').add({
        type: 'eventFollower',
        subject,
        text,
        sentCount: result.data.sentCount,
        followerIds: selectedIds,
        tenantId: tenant?.id,
        createdBy: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        },
        createdAt: new Date()
      });

      toast.success(`${result.data.sentCount}件のメールを送信しました`);
      onClickClose();
    } catch (error) {
      console.error(error);
      toast.error('メール送信に失敗しました');
    }
  };

  useEffect(() => {
    addBreadNavValues({ event });
  }, [event]);

  return (
    event != null && (
      <div>
        <div className="admin-event-followers container-fluid py-5 position-relative">
          <AdminEventHeader activeTab="followers" event={event} user={user} />
          <div className="bg-white p-4">
            <div className="row">
              <div className="col-12">
                <h5 className="text-center">フォロワー一覧</h5>
                <div className="d-flex justify-content-between align-items-end mb-3">
                  <div className="d-flex align-items-end gap-1">
                    <QueryBoolean paramName="showsEntried" label="申込済みも表示" defaultValue={'0'} />
                    <QueryBoolean paramName="unpurchasedOnly" label="未購入のみ" defaultValue={'0'} />
                  </div>
                  {isSelecting && (
                    <ModalButton Modal={ModelFormModal} modalProps={{
                      title: '一括メール送信',
                      submitLabel: '送信',
                      fields: {
                        subject: {
                          label: '件名',
                          type: 'string',
                          validations: {
                            required: _ => !isEmpty(_),
                          },
                        },
                        text: {
                          label: '本文',
                          type: 'text',
                          validations: {
                            required: _ => !isEmpty(_),
                          },
                        },
                      },
                      onSubmit: onClickSend,
                    }}>
                      {selectedIds.length}件にメール送信
                    </ModalButton>
                  )}
                </div>
                <div>
                  {numeral(rows.length).format()}件
                </div>
                {rows.length > 0 ? (
                  <table className="table">
                    <thead className="thead-light text-center">
                      <tr>
                        <th style={{ minWidth: 50 }}>
                          <Input
                            type="checkbox"
                            className="position-relative m-0"
                            checked={isAllSelected}
                            onChange={_ => isAllSelected ? setSelectedIds([]) : setSelectedIds(rows.map(_ => _.id))}
                          />
                        </th>
                        <th style={{ minWidth: 100 }}>フォロー開始日時</th>
                        <th style={{ minWidth: 100 }}>アカウント名</th>
                        <th style={{ minWidth: 100 }}>メールアドレス</th>
                        <th style={{ minWidth: 100 }}>
                          <div>
                            本体購入
                          </div>
                          <div>
                            {isLoadingOrders ? <span className="spinner-border spinner-border-sm" /> : numeral(rows.filter(_ => hasBodyOrderByUid[_.createdBy.uid]).length).format()} / {numeral(rows.length).format()}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        const { id, createdAt, createdBy } = row;
                        return (
                          <tr key={i}>
                            <td>
                              <Input
                                type="checkbox"
                                className="position-relative m-0"
                                checked={selectedIds.includes(id)}
                                onChange={_ => selectedIds.includes(id) ? unselectId(id) : selectId(id)}
                              />
                            </td>
                            <td>{createdAt.toDate().toLocaleString()}</td>
                            <td>
                              <TenantLink to={`/admin/users/${createdBy.uid}`}>{createdBy.displayName}</TenantLink>
                            </td>
                            <td>{createdBy.email}</td>
                            <td className="text-center">{hasBodyOrderByUid[createdBy.uid] ? '有' : ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div>フォロワーはまだいません</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
});
